/**
 * Cache Storage wrapper for the compressed Sapling params shards.
 *
 * Cache Storage is the same surface a Service Worker uses but is fully
 * accessible from the main thread (and Web Workers). For ZcashWallet we
 * don't actually need a Service Worker — we just need persistent
 * fetch-and-cache for the ~26 MB of compressed params. Skipping the SW
 * also dodges its trickier moments (registration race, Telegram
 * WebView's iffy SW lifecycle on first-launch, etc.).
 *
 * Two-tier flow lives elsewhere; this module only owns the wire +
 * Cache-Storage tier:
 *
 *   network → Cache Storage (compressed bytes, immutable)
 *                                                 ↓
 *                                Web Worker decompresses (params/decompress-worker)
 *                                                 ↓
 *                              IndexedDB (canonical uncompressed bytes)  — see params/storage.ts
 *                                                 ↓
 *                              WASM prover loads from IDB on each send
 */

import { SAPLING_PARAMS_BASE_URL } from "../config";
import { PARAM_FILES, type ParamKind } from "./hashes";

const CACHE_NAME = "telecrypt-params-v1";

const compressedUrl = (kind: ParamKind): string =>
  `${SAPLING_PARAMS_BASE_URL.replace(/\/+$/, "")}/${PARAM_FILES[kind].compressed}`;

/**
 * Fetch a compressed shard and store it in Cache Storage. Idempotent —
 * if the shard is already cached, returns immediately without re-hitting
 * the network. Designed to be called from the post-mnemonic onboarding
 * step so the network cost is hidden behind setup UX.
 */
export async function prefetchCompressedShard(
  kind: ParamKind,
  opts?: { onProgress?: (loaded: number, total: number | null) => void; signal?: AbortSignal },
): Promise<void> {
  if (typeof caches === "undefined") {
    throw new Error("Cache Storage unavailable in this environment");
  }
  const cache = await caches.open(CACHE_NAME);
  const url = compressedUrl(kind);

  const cached = await cache.match(url);
  if (cached) return; // already cached; nothing to do

  const res = await fetch(url, { cache: "force-cache", signal: opts?.signal });
  if (!res.ok) {
    throw new Error(`fetch ${url} -> HTTP ${res.status}`);
  }

  // Tee the response so we can read it for progress AND cache it.
  if (opts?.onProgress && res.body) {
    const total = Number(res.headers.get("content-length")) || null;
    const reader = res.body.getReader();
    const chunks: Uint8Array[] = [];
    let loaded = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        chunks.push(value);
        loaded += value.byteLength;
        opts.onProgress(loaded, total);
      }
    }
    const buf = new Uint8Array(loaded);
    let off = 0;
    for (const c of chunks) {
      buf.set(c, off);
      off += c.byteLength;
    }
    const synthetic = new Response(buf, {
      status: 200,
      headers: {
        "content-type": "application/octet-stream",
        "content-length": String(buf.byteLength),
        // Caller paid the network cost; mark immutable so we don't
        // re-fetch on follow-up prefetch calls.
        "cache-control": "public, max-age=31536000, immutable",
      },
    });
    await cache.put(url, synthetic);
    return;
  }

  await cache.put(url, res.clone());
}

/**
 * Read the compressed shard's bytes back out of Cache Storage. Throws
 * if the shard isn't cached yet (caller should call
 * `prefetchCompressedShard` first).
 */
export async function readCompressedShard(kind: ParamKind): Promise<Uint8Array> {
  const cache = await caches.open(CACHE_NAME);
  const res = await cache.match(compressedUrl(kind));
  if (!res) {
    throw new Error(`compressed shard ${kind} not in Cache Storage — call prefetch first`);
  }
  return new Uint8Array(await res.arrayBuffer());
}

/** Quick presence check; doesn't read the body. */
export async function hasCompressedShard(kind: ParamKind): Promise<boolean> {
  if (typeof caches === "undefined") return false;
  const cache = await caches.open(CACHE_NAME);
  const res = await cache.match(compressedUrl(kind));
  return !!res;
}

/**
 * Nuke the compressed cache (debug / recovery). Use after a SHA-256
 * mismatch so the next prefetch hits the network and reloads clean.
 */
export async function clearCompressedCache(): Promise<void> {
  if (typeof caches === "undefined") return;
  await caches.delete(CACHE_NAME);
}
